import React, { useState } from 'react';
import { Helmet } from "react-helmet-async";
import { LuCircleCheck, LuUpload } from "react-icons/lu";
import { auth, db, storage } from '../utils/firebase';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { getDocs, query, collection, where, setDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, Link } from 'react-router-dom';

const inputCls = "w-full border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-ring bg-card text-foreground placeholder:text-[var(--color-muted-foreground)] text-sm transition-colors";
const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";
const errCls   = "text-red-500 text-xs mt-1";

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-black text-[var(--color-muted-foreground)] uppercase tracking-widest mb-4 pb-2 border-b border-border">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, name, type = "text", placeholder, value, onChange, error }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input type={type} name={name} placeholder={placeholder} className={inputCls} value={value} onChange={onChange} />
    {error && <p className={errCls}>{error}</p>}
  </div>
);

function Register() {
  const [form, setForm] = useState({ name:'', username:'', email:'', password:'', phone:'', dob:'', gender:'', address:'', city:'', state:'', zip:'', businessName:'', taxId:'' });
  const [permitFile, setPermitFile]   = useState(null);
  const [permitPreview, setPermitPreview] = useState(null);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading]         = useState(false);
  const navigate = useNavigate();
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
  const isWeakPassword = form.password.length > 0 && !passwordRegex.test(form.password);

  const validate = () => {
    const errors = {};
    Object.entries(form).forEach(([k, v]) => { if (!v.trim()) errors[k] = 'Required'; });
    if (!/^\d{10}$/.test(form.phone)) errors.phone = 'Must be 10 digits';
    if (!passwordRegex.test(form.password)) errors.password = 'Min 8 chars with an uppercase letter, number, and special character';
    if (!permitFile) errors.permit = 'Required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFieldErrors({ ...fieldErrors, [e.target.name]: '' });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setPermitFile(file);
    setFieldErrors({ ...fieldErrors, permit: '' });
    if (file?.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setPermitPreview(reader.result);
      reader.readAsDataURL(file);
    } else setPermitPreview(null);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!validate()) return;
    setLoading(true);
    try {
      const usernameSnap = await getDocs(query(collection(db, 'usernames'), where('__name__', '==', form.username)));
      if (!usernameSnap.empty) { setError('Username already in use'); setLoading(false); return; }
      const { user } = await createUserWithEmailAndPassword(auth, form.email, form.password);
      await updateProfile(user, { displayName: form.name });
      await sendEmailVerification(user);
      const fileRef = ref(storage, `permits/${user.uid}/${permitFile.name}`);
      await uploadBytes(fileRef, permitFile);
      const permitUrl = await getDownloadURL(fileRef);
      await setDoc(doc(db, 'users', user.uid), { uid: user.uid, ...form, permitUrl, status: 'pending' });
      await setDoc(doc(db, 'usernames', form.username), { uid: user.uid, email: form.email });
      await auth.signOut();
      setSuccess('Application submitted! Please check your email to verify your address. Once verified, our team will review your documents and activate your account.');
    } catch (err) {
      const friendlyErrors = {
        'auth/email-already-in-use':    'An account with this email already exists.',
        'auth/invalid-email':           'Please enter a valid email address.',
        'auth/weak-password':           'Password is too weak. Please choose a stronger one.',
        'auth/network-request-failed':  'Network error. Please check your connection and try again.',
        'auth/too-many-requests':       'Too many attempts. Please wait a moment and try again.',
        'auth/operation-not-allowed':   'Account registration is currently unavailable.',
      };
      setError(friendlyErrors[err.code] || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>Apply for Wholesale Access | Khoja Distributions — DFW, Texas</title>
        <meta name="description" content="Apply for a wholesale account with Khoja Distributions. Access bulk pricing on vapes, kratom, delta products, glass, and novelty items for your DFW retail store." />
        <link rel="canonical" href="https://www.khojadistributions.com/register" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Header banner */}
      <div className="bg-brand-navy px-6 py-10 text-center">
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
              <img src="/images/croppedLogo.png" alt="Khoja" className="h-9 w-auto" />
            </div>
            <div className="flex flex-col leading-none text-left">
              <span className="text-white font-black text-[16px] tracking-tight" style={{ fontFamily: "Lexend, sans-serif" }}>Khoja</span>
              <span className="text-brand-amber font-bold text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ fontFamily: "Lexend, sans-serif" }}>Distributions</span>
            </div>
          </Link>
        </div>
        <span className="inline-block bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
          Wholesale Application
        </span>
        <h1 className="text-3xl font-black text-white tracking-tight">Apply for Wholesale Access</h1>
        <p className="text-slate-400 mt-2 text-sm max-w-lg mx-auto">
          Complete the form below to open your B2B account. Verification email will be sent on submission.
        </p>
      </div>

      {/* Form card */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {error   && <div className="mb-6 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm px-4 py-3 rounded-xl">{error}</div>}
        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/50 text-green-700 dark:text-green-400 text-sm px-5 py-4 rounded-xl flex items-start gap-3">
            <LuCircleCheck size={20} className="flex-shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
            {success}
          </div>
        )}

        <form onSubmit={handleRegister} className="bg-card rounded-2xl border border-border shadow-sm p-8 space-y-8">
          <Section title="Personal Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name"  name="name"     placeholder="Jane Smith"   value={form.name}     onChange={handleChange} error={fieldErrors.name} />
              <Field label="Username"   name="username" placeholder="janesmith"        value={form.username} onChange={handleChange} error={fieldErrors.username} />
              <Field label="Email"      name="email"    type="email"     placeholder="jane@business.com" value={form.email}    onChange={handleChange} error={fieldErrors.email} />
              <div>
                <label className={labelCls}>Password</label>
                <input type="password" name="password" placeholder="Min. 6 characters" className={inputCls} value={form.password} onChange={handleChange} />
                {isWeakPassword && <p className="text-yellow-500 text-xs mt-1">Password must be at least 6 characters.</p>}
                {fieldErrors.password && <p className={errCls}>{fieldErrors.password}</p>}
              </div>
            </div>
          </Section>

          <Section title="Contact Details">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Phone Number" name="phone" type="tel"  placeholder="2145551234" value={form.phone} onChange={handleChange} error={fieldErrors.phone} />
              <Field label="Date of Birth" name="dob" type="date"                           value={form.dob}   onChange={handleChange} error={fieldErrors.dob} />
              <div>
                <label className={labelCls}>Gender</label>
                <select name="gender" className={inputCls} value={form.gender} onChange={handleChange}>
                  <option value="">Select...</option>
                  <option>Male</option><option>Female</option><option>Other</option>
                </select>
                {fieldErrors.gender && <p className={errCls}>{fieldErrors.gender}</p>}
              </div>
            </div>
          </Section>

          <Section title="Business Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Business Name (DBA)"          name="businessName" placeholder="Smith Convenience LLC" value={form.businessName} onChange={handleChange} error={fieldErrors.businessName} />
              <Field label="Tax Identification Number"    name="taxId"        placeholder="12-3456789"            value={form.taxId}        onChange={handleChange} error={fieldErrors.taxId} />
            </div>
            <div>
              <label className={labelCls}>Tax Permit / Business License</label>
              <div className={`${inputCls} flex items-center justify-between cursor-pointer`} onClick={() => document.getElementById('permit-upload').click()}>
                <span className={permitFile ? "text-foreground" : "text-[var(--color-muted-foreground)]"}>
                  {permitFile ? permitFile.name : "Click to upload image or PDF..."}
                </span>
                <LuUpload size={16} className="text-slate-400" />
              </div>
              <input id="permit-upload" type="file" accept="image/*,.pdf" onChange={handleFileChange} className="hidden" />
              {fieldErrors.permit && <p className={errCls}>{fieldErrors.permit}</p>}
              {permitPreview && <img src={permitPreview} alt="Permit Preview" className="mt-3 max-h-24 rounded-lg object-contain border border-border" />}
            </div>
          </Section>

          <Section title="Shipping Address">
            <Field label="Street Address" name="address" placeholder="123 Main St" value={form.address} onChange={handleChange} error={fieldErrors.address} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="City"     name="city"  placeholder="Dallas" value={form.city}  onChange={handleChange} error={fieldErrors.city} />
              <Field label="State"    name="state" placeholder="TX"     value={form.state} onChange={handleChange} error={fieldErrors.state} />
              <Field label="ZIP Code" name="zip"   placeholder="75001"  value={form.zip}   onChange={handleChange} error={fieldErrors.zip} />
            </div>
          </Section>

          <button type="submit" disabled={loading} className="w-full bg-brand-navy hover:bg-brand-amber dark:hover:bg-accent disabled:opacity-50 transition-colors text-white font-bold py-4 rounded-xl text-sm uppercase tracking-widest">
            {loading ? "Creating Account…" : "Create Partner Account"}
          </button>
        </form>

        <p className="text-sm text-center mt-6 text-[var(--color-muted-foreground)]">
          Already have an account?{" "}
          <Link to="/login" className="text-foreground font-bold hover:text-accent transition-colors">Sign In</Link>
        </p>
      </div>
    </div>
  );
}

export default Register;

